/**
 * Formatting helpers used across the UI. Kept dependency-free
 * (Intl.NumberFormat is a native browser API, no library needed) and
 * consistent so currency/date formatting never drifts between screens.
 *
 * This is the SINGLE currency-formatting utility for the whole app (see
 * CurrencyInput.tsx, which is the single input component built on top of
 * it) — there should be no other place in the codebase that formats or
 * parses money independently.
 */

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const inrFormatterPrecise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Same as the two above, but without the currency symbol — used inside
// editable inputs, where showing "₹" as part of the live-typed value is
// awkward on mobile keyboards; the ₹ symbol is instead shown as a fixed
// prefix alongside the input (see CurrencyInput.tsx).
const inrGroupingOnly = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 0,
});

const inrGroupingOnlyPrecise = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatInr(amount: number, precise = false): string {
  return precise ? inrFormatterPrecise.format(amount) : inrFormatter.format(amount);
}

/**
 * Formats a number with Indian (lakh/crore) digit grouping but WITHOUT
 * the ₹ currency symbol — for use inside editable text inputs.
 */
export function formatInrGrouped(amount: number, precise = false): string {
  return precise ? inrGroupingOnlyPrecise.format(amount) : inrGroupingOnly.format(amount);
}

/**
 * Parses a user-typed, possibly Indian-grouped monetary string (e.g.
 * "35,00,000", "₹35,00,000.50", "3500000") back into a plain finite
 * number. Strips the currency symbol and any grouping separators, then
 * parses what remains as a decimal number.
 *
 * Returns `undefined` for empty input or anything that doesn't parse to
 * a finite number, so callers can distinguish "not entered yet" /
 * "invalid" from a genuine 0. This is the ONLY place amount-parsing
 * logic lives — every money field in the app should round-trip through
 * this function and formatInrGrouped(), never a bespoke regex elsewhere.
 */
export function parseInrInput(value: string): number | undefined {
  const cleaned = value.replace(/[₹,\s]/g, '').trim();
  if (cleaned === '') return undefined;
  // Reject anything that isn't a plain (optionally negative, optionally
  // decimal) number after stripping grouping — guards against stray
  // characters a mobile keyboard/autocorrect might insert.
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

export function formatDate(isoDate: string | undefined): string {
  if (!isoDate) return '—';
  const [year, month, day] = isoDate.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function formatDateTime(isoDateTime: string | undefined): string {
  if (!isoDateTime) return '—';
  const d = new Date(isoDateTime);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
